import React from 'react';
import styled from 'styled-components';

import {
  BUTTON_THEMES,
  BUTTON_TYPES,
  ICON_TYPES,
} from '../../../shared/constants';
import { ICard } from '../../../types/shared';

import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import { DEQ_COMPS, DEQ_TYPES } from '../../../utils/deq';
import { getCtaLink, ReactMarkdownExtended } from '../../../utils/generic';
import { replaceUtmParam } from '../../../utils/string-utils/string-utils';

import mq from '../../../styles/breakpoints';
import buttonStyles from '../../../styles/buttonStyles';
import { Body, IntroText } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

interface WizardResultsHeaderProps extends ICard {
  onResetButtonClick: () => void;
  extraLinkParams?: string;
}

const WizardResultsHeader = ({
  title,
  body,
  image,
  primaryLink,
  secondaryLink,
  onResetButtonClick,
  extraLinkParams,
}: WizardResultsHeaderProps) => {
  // Maybe fix this with context to make methods in scope of wizard available.
  const handleOnButtonClick = () => {
    onResetButtonClick();
  };

  const primaryCta = primaryLink ? getCtaLink(primaryLink) : undefined;
  const secondaryCta = secondaryLink ? getCtaLink(secondaryLink) : undefined;

  const primaryCtaOptionalParams =
    extraLinkParams && primaryLink
      ? replaceUtmParam(primaryCta as string, extraLinkParams)
      : primaryCta;

  const secondaryCtaOptionalParams =
    extraLinkParams && secondaryLink
      ? replaceUtmParam(secondaryCta as string, extraLinkParams)
      : secondaryCta;

  return (
    <ResultsHeaderWrapper>
      <StyledImage src={image?.file?.url} />
      <div>
        {title && <StyledIntroText aria-level={2}>{title}</StyledIntroText>}
        {body && (
          <Body>
            <ReactMarkdownExtended componentType={DEQ_COMPS.wizardResultHeader}>
              {body.childMarkdownRemark.rawMarkdownBody}
            </ReactMarkdownExtended>
          </Body>
        )}
        {/* TODO: Check if we can render the call to actions a bit cleaner */}
        <ButtonWrapper>
          {primaryLink &&
            (primaryLink.linkedPage ||
            primaryLink.externalUrl ||
            primaryLink.asset ? (
              <StyledPrimaryButton
                type={primaryLink?.type}
                label={primaryLink.label || primaryLink.name}
                buttonTheme={primaryLink.theme || BUTTON_THEMES.highlight}
                to={primaryCtaOptionalParams}
                iconPosition={primaryLink?.iconPosition}
                icon={primaryLink?.icon}
                deqComponentTitle={primaryLink.label}
                deqCustomAttributes={{
                  'component-type': DEQ_TYPES.topResultsCta,
                  type: DEQ_TYPES.textLink,
                }}
                target="_blank"
              />
            ) : (
              <StyledPrimaryButton
                type={BUTTON_TYPES.internal}
                icon={ICON_TYPES.none}
                buttonTheme={BUTTON_THEMES.highlight}
                label={primaryLink.label}
                onButtonClick={handleOnButtonClick}
              />
            ))}
          {secondaryLink &&
            (secondaryLink.linkedPage ||
            secondaryLink.externalUrl ||
            secondaryLink.asset ? (
              <StyledSecondaryButton
                type={secondaryLink?.type}
                label={secondaryLink.label || secondaryLink.name}
                buttonTheme={secondaryLink.theme || BUTTON_THEMES.default}
                to={secondaryCtaOptionalParams}
                iconPosition={secondaryLink?.iconPosition}
                icon={secondaryLink?.icon}
                deqComponentTitle={secondaryLink.label}
                deqCustomAttributes={{
                  type: DEQ_TYPES.textLink,
                }}
                target="_blank"
              />
            ) : (
              <StyledSecondaryButton
                type={BUTTON_TYPES.internal}
                buttonAction={BUTTON_TYPES.action}
                icon={ICON_TYPES.chevron}
                buttonTheme={BUTTON_THEMES.default}
                label={secondaryLink.label}
                onButtonClick={handleOnButtonClick}
              />
            ))}
        </ButtonWrapper>
      </div>
    </ResultsHeaderWrapper>
  );
};

const ResultsHeaderWrapper = styled.div`
  display: flex;
  gap: 28px;
  margin: ${Spacings.S} 0 48px;

  ${mq.to.breakpoint.M`
    margin: ${Spacings.S} ${Spacings.S} ${Spacings.S};
  `}
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.M};
  margin-top: ${Spacings.S};

  ${mq.from.breakpoint.M`
    flex-direction: row;
  `}
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  ${buttonStyles.primaryButtonPosition}
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  ${buttonStyles.secondaryButtonPosition}
`;

const StyledImage = styled.img`
  display: none;

  ${mq.from.breakpoint.M`
    width: 80px;
    display: block
  `}
`;

const StyledIntroText = styled(IntroText)`
  display: block;
  margin-bottom: ${Spacings.XS};
`;

export default WizardResultsHeader;
